import { useState, useEffect } from 'react';
import { message, Tooltip } from 'antd';
import {
    IconArrowL, IconArrowR, IconCheck, IconMusic, IconClock,
    IconPdf, IconPlay, IconUser, IconPeople,
    IconChevD, initials
} from './Icons';

/**
 * Convert videoDuration (stored in seconds) to mm:ss display format
 */
const formatVideoDuration = (seconds) => {
    if (!seconds || isNaN(seconds)) return '—';
    const totalSec = Math.round(Number(seconds));
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

/**
 * Assessment Form — simplified scoring view.
 * Single score input (0-100) + feedback + comments.
 */
const AssessmentForm = ({
    registrant,
    existingScore,
    isFinalized = false,
    onBack,
    onSave,
    saving,
    onOpenVideo,
    onOpenPDF,
    loggedInUser,
    pendingQueue,
    onNext,
}) => {
    const performer = registrant.performers?.[0];
    const name = performer?.fullName ||
        `${performer?.firstName || ''} ${performer?.lastName || ''}`.trim();
    const isEnsemble = registrant.PerformanceCategory === 'Ensemble';

    // Score state
    const [score, setScore] = useState(existingScore?.score ?? '');
    const [feedback, setFeedback] = useState(existingScore?.comment || '');
    const [comments, setComments] = useState(existingScore?.panelComment || '');
    const [showMembers, setShowMembers] = useState(false);

    // Reset when registrant changes
    useEffect(() => {
        setScore(existingScore?.score ?? '');
        setFeedback(existingScore?.comment || '');
        setComments(existingScore?.panelComment || '');
    }, [registrant.id]);

    const handleScoreChange = (e) => {
        const raw = e.target.value;
        if (raw === '') { setScore(''); return; }
        const v = parseInt(raw, 10);
        if (!isNaN(v)) setScore(Math.max(0, Math.min(100, v)));
    };

    const handleSave = () => {
        const finalScore = score === '' ? null : Number(score);
        if (finalScore === null || finalScore === undefined) {
            message.warning('Please enter a score before saving');
            return;
        }
        if (finalScore < 0 || finalScore > 100) {
            message.error('Score must be between 0 and 100');
            return;
        }
        onSave(registrant, {
            score: finalScore,
            comment: feedback,
            panelComment: comments,
        });
    };

    return (
        <div style={{ animation: 'jdScreenIn 0.22s cubic-bezier(.2,.8,.3,1)', flex: 1, display: 'flex', flexDirection: 'column' }}>
            {/* Breadcrumb */}
            <div className="jd-crumbs">
                <a onClick={onBack}>Dashboard</a>
                <span className="jd-crumb-sep">/</span>
                <span className="jd-crumb-current">{name}</span>
                <div className="jd-grow" />
            </div>

            {/* Finalized banner */}
            {isFinalized && (
                <div style={{
                    background: '#fef9e7', border: '1px solid #f0d97a', borderRadius: 8,
                    padding: '10px 20px', margin: '0 28px 0', display: 'flex',
                    alignItems: 'center', gap: 10, fontSize: 13, color: '#7a6a1f',
                }}>
                    <span style={{ fontSize: 16 }}>🔒</span>
                    <span><strong>This assessment has been finalized.</strong> Scores are locked and can no longer be edited.</span>
                </div>
            )}

            {/* Compact info strip */}
            <div className="jd-info-strip">
                <div className="jd-info-strip-left">
                    <span className="jd-avatar lg">{initials(name)}</span>
                    <div>
                        <div style={{ fontWeight: 600, fontSize: 18, letterSpacing: '-0.01em' }}>{name}</div>
                        <div className="jd-row" style={{ gap: 6, marginTop: 6 }}>
                            <span className="jd-pill">
                                {isEnsemble ? <IconPeople size={11} /> : <IconUser size={11} />}
                                {isEnsemble ? (
                                    <Tooltip 
                                        title={registrant.performers?.map(perf => perf.fullName || `${perf.firstName || ''} ${perf.lastName || ''}`.trim()).join(', ')}
                                        placement="bottomLeft"
                                    >
                                        <span style={{ cursor: 'help', borderBottom: '1px dotted var(--jd-ink-3)' }}>
                                            Group · {registrant.performers?.length || 0} members
                                        </span>
                                    </Tooltip>
                                ) : 'Individual'}
                            </span>
                            <span className="jd-pill">{registrant.ageCategory || registrant.competitionCategory}</span>
                        </div>
                    </div>
                </div>
                <div className="jd-info-strip-right">
                    {registrant.repertoire && (
                        <span><IconMusic size={13} style={{ color: 'var(--jd-ink-3)' }} /> {registrant.repertoire}</span>
                    )}
                    {registrant.videoDuration && (
                        <span style={{ marginLeft: 8, color: 'var(--jd-ink-3)', fontSize: 13 }}>
                            <IconClock size={12} style={{ verticalAlign: '-1px', marginRight: 4 }} />
                            {formatVideoDuration(registrant.videoDuration)}
                        </span>
                    )}
                    <button
                        className="jd-link"
                        onClick={() => onOpenPDF(registrant.pdfRepertoireS3Link)}
                        disabled={!registrant.pdfRepertoireS3Link}
                        style={{ opacity: registrant.pdfRepertoireS3Link ? 1 : 0.4, marginLeft: 12 }}
                    >
                        <IconPdf size={13} /> Sheet music
                    </button>
                    <button
                        className="jd-link"
                        onClick={() => onOpenVideo(registrant.videoPerformanceS3Link)}
                        disabled={!registrant.videoPerformanceS3Link}
                        style={{ opacity: registrant.videoPerformanceS3Link ? 1 : 0.4, marginLeft: 12, marginRight: 4 }}
                    >
                        <IconPlay size={13} /> Watch video
                    </button>
                    {isEnsemble && registrant.performers?.length > 1 && (
                        <div style={{ position: 'relative' }}>
                            <button
                                className="jd-btn sm ghost"
                                onClick={() => setShowMembers(s => !s)}
                            >
                                Members ({registrant.performers.length}) <IconChevD size={12} />
                            </button>
                            {showMembers && (
                                <div className="jd-box" style={{
                                    position: 'absolute', right: 0, top: 'calc(100% + 4px)',
                                    padding: 12, minWidth: 200, zIndex: 4,
                                    boxShadow: '0 8px 20px rgba(0,0,0,.08)'
                                }}>
                                    {registrant.performers.map((m, i) => (
                                        <div key={i} className="jd-row" style={{ gap: 8, padding: '4px 0', fontSize: 12 }}>
                                            <span className="jd-avatar sm">
                                                {initials(m.fullName || `${m.firstName} ${m.lastName}`)}
                                            </span>
                                            {m.fullName || `${m.firstName} ${m.lastName}`}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Form body */}
            <div style={{ padding: '24px 32px', maxWidth: 980, margin: '0 auto', width: '100%', flex: 1 }}>
                {/* Performance feedback — full width */}
                <div className="jd-box" style={{ padding: 16 }}>
                    <div className="jd-row jd-between" style={{ marginBottom: 8 }}>
                        <span className="jd-label">Performance feedback (for performer)</span>
                        <span className="jd-label" style={{ textTransform: 'none', letterSpacing: 0 }}>{feedback.length} / 600</span>
                    </div>
                    <textarea
                        className="jd-textarea"
                        value={feedback}
                        maxLength={600}
                        onChange={e => setFeedback(e.target.value)}
                        placeholder="Notes on tone, dynamics, expression, technical accuracy…"
                        style={{ minHeight: 120 }}
                        disabled={isFinalized}
                    />
                </div>

                {/* Notes + Overall score — side by side */}
                <div className="jd-row" style={{ gap: 14, marginTop: 14, alignItems: 'stretch' }}>
                    <div className="jd-box jd-grow" style={{ padding: 16 }}>
                        <div className="jd-row jd-between" style={{ marginBottom: 8 }}>
                            <span className="jd-label">Notes (for APCS team)</span>
                            <span className="jd-label" style={{ textTransform: 'none', letterSpacing: 0 }}>optional</span>
                        </div>
                        <textarea
                            className="jd-textarea"
                            value={comments}
                            onChange={e => setComments(e.target.value)}
                            placeholder="Anything else for the APCS team…"
                            style={{ minHeight: 90 }}
                            disabled={isFinalized}
                        />
                    </div>

                    <div className="jd-box" style={{ padding: 16, flex: '0 0 320px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                        <label className="jd-label" htmlFor="score-input" style={{ marginBottom: 10 }}>Overall score</label>
                        <div className="jd-row" style={{ alignItems: 'center', gap: 10 }}>
                            <input
                                id="score-input"
                                type="number"
                                className="jd-input jd-score-input"
                                min={0} max={100} step={1}
                                value={score}
                                placeholder="0"
                                onChange={handleScoreChange}
                                style={{ width: 120, fontSize: 16, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
                                disabled={isFinalized}
                            />
                            <span style={{ fontSize: 14, color: 'var(--jd-ink-3)' }}>/ 100</span>
                        </div>
                    </div>
                </div>

                {/* Action bar */}
                <div className="jd-box jd-row jd-between" style={{ padding: '12px 16px', marginTop: 16, background: 'var(--jd-paper-2)' }}>
                    <button className="jd-btn ghost" onClick={onBack}>
                        <IconArrowL size={13} /> Back
                    </button>
                    <div className="jd-row" style={{ gap: 10 }}>
                        {pendingQueue && (
                            <span className="jd-label" style={{ textTransform: 'none', letterSpacing: 0, fontSize: 12 }}>
                                {pendingQueue.position} of {pendingQueue.total} pending
                            </span>
                        )}
                        <button
                            className="jd-btn"
                            onClick={onNext}
                            disabled={!pendingQueue || pendingQueue.position === pendingQueue.total}
                        >
                            Next participant <IconArrowR size={13} />
                        </button>
                        <button className="jd-btn primary" onClick={handleSave} disabled={saving || isFinalized}>
                            {isFinalized ? '🔒 Finalized' : saving ? 'Saving...' : <><IconCheck size={14} /> Save assessment</>}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AssessmentForm;
